import { useEffect, useState } from 'react';

/**
 * True once the tab has been away long enough to count as a trip to the bank app, and has come back.
 *
 * The threshold is what separates a trip from a blink: switching apps, an incoming call or an
 * accidental swipe hide the page for a moment, and greeting the client after each of them turns the
 * banner into noise they stop reading — including the time it actually matters.
 *
 * The shared visit-outcome tracker measures the same interval already, but it reports it to the
 * analytics channel and exposes no state to render, and it is also loaded by the other templates.
 */
export const useReturnedToTab = (awayThresholdMs = 10_000) => {
  const [returned, setReturned] = useState(false);

  useEffect(() => {
    let hiddenAt: number | null = null;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt !== null && Date.now() - hiddenAt >= awayThresholdMs) {
        setReturned(true);
      }
      hiddenAt = null;
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [awayThresholdMs]);

  return { returned, dismiss: () => setReturned(false) };
};
